/**
 * MeasurementOverlay — renders measurement points and connecting lines
 * directly onto the MapLibre map via GeoJSON source/layers, and manages
 * the cursor style so the user sees a crosshair while in measurement mode.
 *
 * This component reads:
 *   - `usePluginStore().activePluginId` to know whether measurement is active
 *   - Props `points` for the current measurement vertices
 *   - Props `container` to access the MapLibre instance
 *
 * It adds/removes a GeoJSON source (`__measure-overlay`) and associated
 * layers (`__measure-points`, `__measure-line`) when active. On unmount
 * or mode change, it cleans up after itself.
 *
 * Design-system compliance (WP-3 refactor):
 *   - Every GeoJSON paint property now routes through the design bridge
 *     (`design/tokenResolver.ts`). The previous raw warning / error
 *     colours mapped to dark-mode tokens, but the map canvas is always
 *     **light** per `UIUX_DETAILED_SURFACE_STUDY.md` §3 — we now consume
 *     the dedicated `state-warning-on-canvas` /
 *     `state-error-on-canvas` resolvers.
 *   - The previous white text-color (unreadable on a light canvas) is
 *     replaced by `text-on-canvas-light` (dark text + light halo) so the
 *     index labels stay AA against any basemap.
 */

import { useEffect, useRef } from 'react';
import { usePluginStore } from '@/state/usePluginStore';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import {
  resolveStateErrorOnCanvas,
  resolveStateWarningOnCanvas,
  resolveTextHaloOnCanvasLight,
  resolveTextOnCanvasLight,
} from '@/design/tokenResolver';

const SOURCE_ID = '__measure-overlay';
const POINTS_LAYER_ID = '__measure-points';
const LINE_LAYER_ID = '__measure-line';
const LABEL_LAYER_ID = '__measure-labels';

function buildGeoJson(points: ReadonlyArray<readonly [number, number]>) {
  const features: GeoJSON.Feature[] = [];

  // Point features with index labels.
  for (let i = 0; i < points.length; i++) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [points[i][0], points[i][1]] },
      properties: { idx: i + 1, label: `P${i + 1}` },
    });
  }

  // Line feature connecting all points.
  if (points.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: points.map(([lon, lat]) => [lon, lat]),
      },
      properties: { kind: 'line' },
    });
  }

  return { type: 'FeatureCollection' as const, features };
}

export interface MeasurementOverlayProps {
  container: HTMLElement | null;
  points: ReadonlyArray<readonly [number, number]>;
}

export function MeasurementOverlay({ container, points }: MeasurementOverlayProps) {
  const activePlugin = usePluginStore((s) => s.activePluginId);
  const isMeasuring = activePlugin === 'measurement';
  const prevCursorRef = useRef<string>('');

  // Manage cursor style.
  useEffect(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    const canvas = map.getCanvas();
    if (isMeasuring) {
      prevCursorRef.current = canvas.style.cursor;
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = prevCursorRef.current || '';
    }
    return () => {
      canvas.style.cursor = prevCursorRef.current || '';
    };
  }, [container, isMeasuring]);

  // Manage GeoJSON overlay source/layers.
  useEffect(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;

    const cleanup = () => {
      try {
        if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
        if (map.getLayer(POINTS_LAYER_ID)) map.removeLayer(POINTS_LAYER_ID);
        if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        /* map may be torn down */
      }
    };

    if (!isMeasuring || points.length === 0) {
      cleanup();
      return;
    }

    const geojson = buildGeoJson(points);

    if (map.getSource(SOURCE_ID)) {
      // Update existing source data.
      const src = map.getSource(SOURCE_ID);
      if (src && 'setData' in src) {
        (src as { setData: (data: unknown) => void }).setData(geojson);
      }
    } else {
      // Create source + layers.
      map.addSource(SOURCE_ID, { type: 'geojson', data: geojson as unknown as GeoJSON.FeatureCollection });

      // GeoJSON paint props resolved from design-bridge tokens at runtime
      // so component code never carries raw #hex literals (the bridge is
      // the single source of truth; see `geosoft/no-raw-visual-constants`
      // ESLint rule).
      const warningOnCanvas = resolveStateWarningOnCanvas();
      const errorOnCanvas = resolveStateErrorOnCanvas();
      const textOnCanvas = resolveTextOnCanvasLight();
      const haloOnCanvas = resolveTextHaloOnCanvasLight();

      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-color': warningOnCanvas,
          'line-width': 3,
          'line-dasharray': [3, 2],
        },
      });

      map.addLayer({
        id: POINTS_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': errorOnCanvas,
          'circle-stroke-color': haloOnCanvas,
          'circle-stroke-width': 2,
        },
      });

      map.addLayer({
        id: LABEL_LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['==', ['geometry-type'], 'Point'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-offset': [0, -1.5],
          'text-anchor': 'bottom',
          'text-font': ['Open Sans Regular'],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': textOnCanvas,
          'text-halo-color': haloOnCanvas,
          'text-halo-width': 1.5,
        },
      });
    }

    return cleanup;
  }, [container, isMeasuring, points]);

  return null;
}
